// Project file with several shots (F-18, D-41). A shot is one comparison set (A + B1…B3) and keeps the 1.0 shot
// content (`Project`); the file wraps a list of shots. 1.0 files open as a project with one shot.

import type { Project } from './types';
import { newId, parseProject, serializeProject } from './project';

export const DOC_SCHEMA_VERSION = '2.0';
/** Shot content keeps the 1.0 layout; it is parsed with the 1.0 reader. */
const SHOT_SCHEMA_VERSION = '1.0';

/** One comparison set. The shot name is `project.name` (also used for export file names). */
export interface Shot {
  id: string;
  project: Project;
}

export interface ProjectDoc {
  schemaVersion: string;
  appVersion: string;
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  shots: Shot[];
  activeShotId?: string;
  /** Unknown top-level fields, kept and written back unchanged. */
  extra?: Record<string, unknown>;
}

const DOC_KEYS = new Set(['schemaVersion', 'appVersion', 'id', 'name', 'createdAt', 'updatedAt', 'shots', 'activeShotId']);

export function createDoc(name: string, appVersion: string, now = new Date()): ProjectDoc {
  const iso = now.toISOString();
  return { schemaVersion: DOC_SCHEMA_VERSION, appVersion, id: newId('proj'), name, createdAt: iso, updatedAt: iso, shots: [] };
}

function compareVersion(a: string, b: string): number {
  const pa = a.split('.').map((x) => parseInt(x, 10) || 0);
  const pb = b.split('.').map((x) => parseInt(x, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function shotToJson(s: Shot, appVersion: string, now: Date): Record<string, unknown> {
  const o = JSON.parse(serializeProject(s.project, appVersion, now)) as Record<string, unknown>;
  delete o.schemaVersion;
  delete o.appVersion;
  return { id: s.id, ...o };
}

export function serializeDoc(doc: ProjectDoc, appVersion: string, now = new Date()): string {
  const out: Record<string, unknown> = {
    ...(doc.extra ?? {}),
    schemaVersion: DOC_SCHEMA_VERSION,
    appVersion,
    id: doc.id,
    name: doc.name,
    createdAt: doc.createdAt,
    updatedAt: now.toISOString(),
    activeShotId: doc.activeShotId,
    shots: doc.shots.map((s) => shotToJson(s, appVersion, now)),
  };
  return JSON.stringify(out, null, 2);
}

/**
 * Content fingerprint for "unsaved changes": the saved JSON without save-time stamps, the playhead position and
 * file dates (moving the playhead or re-linking the same videos is not an edit).
 */
export function docContentKey(doc: ProjectDoc): string {
  type ShotJson = { playback?: { tick?: number }; updatedAt?: string; sources?: { file?: { lastModified?: number } }[] };
  const o = JSON.parse(serializeDoc(doc, '', new Date(0))) as { shots: ShotJson[]; updatedAt?: string; activeShotId?: string };
  delete o.updatedAt;
  delete o.activeShotId;
  for (const s of o.shots) {
    delete s.updatedAt;
    if (s.playback) s.playback.tick = 0;
    // A copy of the same video (e.g. on the NAS) may carry another date; the fingerprint decides.
    for (const src of s.sources ?? []) if (src.file) delete src.file.lastModified;
  }
  return JSON.stringify(o);
}

export type DocParseResult =
  | { ok: true; doc: ProjectDoc; readOnly: boolean; warnings: string[]; migratedFrom?: string }
  | { ok: false; errorKey: string };

/** Reads a 2.0 project file, or a 1.0 file as a project with one shot. */
export function parseDoc(text: string, fallbackName = ''): DocParseResult {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, errorKey: 'error.project.parse' };
  }
  if (!isObject(raw) || typeof raw.schemaVersion !== 'string') return { ok: false, errorKey: 'error.project.invalid' };

  // 1.0: a single comparison set at the top level.
  if (!Array.isArray(raw.shots)) {
    const r = parseProject(text);
    if (!r.ok) return r;
    const name = r.project.name || fallbackName;
    const doc: ProjectDoc = {
      schemaVersion: DOC_SCHEMA_VERSION,
      appVersion: r.project.appVersion,
      id: newId('proj'),
      name,
      createdAt: r.project.createdAt,
      updatedAt: r.project.updatedAt,
      shots: [{ id: newId('shot'), project: r.project }],
    };
    return { ok: true, doc, readOnly: r.readOnly, warnings: r.warnings, migratedFrom: raw.schemaVersion };
  }

  const warnings: string[] = [];
  const readOnly = compareVersion(raw.schemaVersion, DOC_SCHEMA_VERSION) > 0;
  if (readOnly) warnings.push('project.warn.newer');
  const shots: Shot[] = [];
  for (const s of raw.shots) {
    if (!isObject(s)) return { ok: false, errorKey: 'error.project.invalid' };
    const r = parseProject(JSON.stringify({ ...s, schemaVersion: SHOT_SCHEMA_VERSION }));
    if (!r.ok) return r;
    const p = r.project;
    if (p.extra) {
      delete p.extra.id;
      if (!Object.keys(p.extra).length) delete p.extra;
    }
    shots.push({ id: typeof s.id === 'string' && s.id ? s.id : newId('shot'), project: p });
  }
  const extra: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) if (!DOC_KEYS.has(k)) extra[k] = v;
  const now = new Date().toISOString();
  const doc: ProjectDoc = {
    schemaVersion: readOnly ? raw.schemaVersion : DOC_SCHEMA_VERSION,
    appVersion: String(raw.appVersion ?? ''),
    id: typeof raw.id === 'string' && raw.id ? raw.id : newId('proj'),
    name: typeof raw.name === 'string' && raw.name ? raw.name : fallbackName,
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : now,
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : now,
    shots,
    activeShotId: typeof raw.activeShotId === 'string' && shots.some((s) => s.id === raw.activeShotId) ? raw.activeShotId : undefined,
  };
  if (Object.keys(extra).length) doc.extra = extra;
  return { ok: true, doc, readOnly, warnings };
}

// ---------------------------------------------------------------------------------------- media matching

export interface MediaWant {
  name: string;
  size: number;
}

export interface MediaCandidate {
  name: string;
  size: number;
}

const ext = (n: string) => (n.includes('.') ? n.slice(n.lastIndexOf('.')).toLowerCase() : '');

/**
 * Files in a media folder that may be the wanted video, best first. The fingerprint includes the size, so only
 * files of the same size can match; among them the same name comes first, then the same extension. The caller
 * confirms each candidate with the fingerprint.
 */
export function rankCandidates<T extends MediaCandidate>(want: MediaWant, list: readonly T[]): T[] {
  const score = (c: T) => (c.name === want.name ? 0 : c.name.toLowerCase() === want.name.toLowerCase() ? 1 : ext(c.name) === ext(want.name) ? 2 : 3);
  return list.filter((c) => c.size === want.size).sort((a, b) => score(a) - score(b));
}

/** Video file extensions considered when scanning a media folder. */
export const VIDEO_EXTS = ['.mp4', '.mov', '.m4v', '.mkv', '.webm', '.mxf'];

export function isVideoName(name: string): boolean {
  return VIDEO_EXTS.includes(ext(name));
}
